/**
 * Canonical tank & battery metric math.
 *
 * This is the SINGLE source of truth shared by the backend alert engine and the
 * React dashboard. Previously the same formulas were copied into the backend
 * (`metrics-calculator.ts`) and three+ frontend spots, and they had drifted —
 * the dashboard and the alert engine computed materially different numbers.
 *
 * Keep this file DEPENDENCY-FREE (no imports, no Node/DOM APIs beyond Math/Date)
 * so it bundles safely into both the Lambda backend and the browser frontend.
 * It lives under `backend/src/` because the backend's Docker image build context
 * is `backend/` only; the frontend imports it via the `@shared` Vite alias.
 *
 * UNIT CONTRACT (must match firmware/server — do not change unilaterally):
 *   measurement       : millimetres (mm)  — sensor-to-water-surface distance
 *   sensorMountHeight : centimetres (cm)  — sensor height above the tank TOP,
 *                                           so 0 means mounted at the rim and
 *                                           the sensor sits (tank height +
 *                                           mount height) above the floor
 *   tankDimensions.*  : centimetres (cm)
 *   voltage           : volts (V)
 */

export type TankShape = 'cylindrical_vertical' | 'cylindrical_horizontal';

/**
 * How far a reading may fall past the sensor mount height before it is treated
 * as a fault rather than an empty tank. A tank sitting at its floor can read a
 * centimetre or two negative from sensor noise and mounting slop; anything
 * beyond that means the mount height or the sensor is wrong.
 */
const DEPTH_FAULT_TOLERANCE_CM = 5;

export interface TankDimensions {
  diameter?: number; // cm
  height?: number; // cm (vertical) or length (horizontal)
}

export interface UnitConfig {
  tankShape?: string;
  tankDimensions?: TankDimensions;
  sensorMountHeight?: number; // cm
  maxBatteryVoltage?: number; // V
}

export interface TelemetryPoint {
  voltage: number; // V
  measurement: number; // mm
  timestamp: string; // ISO 8601
}

const BATTERY_MIN_V = 3.0;
const BATTERY_MAX_V = 4.2;

/** 1S Li-ion discharge curve: terminal voltage → state-of-charge (%). */
const VOLTAGE_LOOKUP_1S: ReadonlyArray<{ v: number; p: number }> = [
  { v: 4.2, p: 100 },
  { v: 4.1, p: 90 },
  { v: 4.0, p: 80 },
  { v: 3.9, p: 60 },
  { v: 3.8, p: 40 },
  { v: 3.7, p: 20 },
  { v: 3.6, p: 10 },
  { v: 3.3, p: 5 },
  { v: 3.0, p: 0 },
];

/**
 * Battery state-of-charge (%) from terminal voltage using the non-linear Li-ion
 * discharge curve (more accurate than a linear approximation, which over-reads
 * in the flat 3.7-4.0 V region). The curve is scaled when a unit reports a
 * non-default full-charge voltage.
 */
export function getBatteryCapacity(voltage: number, maxBatteryVoltage: number = BATTERY_MAX_V): number {
  const maxV = maxBatteryVoltage || BATTERY_MAX_V;
  if (voltage >= maxV) return 100;
  if (voltage <= BATTERY_MIN_V) return 0;

  const scaleRatio = (maxV - BATTERY_MIN_V) / (BATTERY_MAX_V - BATTERY_MIN_V);
  const scaled = VOLTAGE_LOOKUP_1S.map((item) =>
    item.v === BATTERY_MIN_V ? item : { v: BATTERY_MIN_V + (item.v - BATTERY_MIN_V) * scaleRatio, p: item.p },
  );

  for (let i = 0; i < scaled.length - 1; i++) {
    const upper = scaled[i];
    const lower = scaled[i + 1];
    if (voltage <= upper.v && voltage > lower.v) {
      const ratio = (voltage - lower.v) / (upper.v - lower.v);
      return Math.round(lower.p + ratio * (upper.p - lower.p));
    }
  }
  return 0;
}

/** Hours between two ISO timestamps (later - earlier). 0 if either is invalid. */
export function hoursBetween(laterIso: string, earlierIso: string): number {
  const a = new Date(laterIso).getTime();
  const b = new Date(earlierIso).getTime();
  if (!isFinite(a) || !isFinite(b)) return 0;
  return (a - b) / (1000 * 60 * 60);
}

/** Battery charge rate in V/hr (positive = charging). 0 if no/!valid previous point. */
export function computeChargeRate(current: TelemetryPoint, previous: TelemetryPoint | null): number {
  if (!previous) return 0;
  const hours = hoursBetween(current.timestamp, previous.timestamp);
  if (hours <= 0) return 0;
  return (current.voltage - previous.voltage) / hours;
}

/**
 * Estimated days of remaining battery life from the capacity drain between two
 * points. Returns null when charging/steady or when inputs are unusable.
 */
export function estimateBatteryLifeDays(
  currentCapacityPct: number,
  previousCapacityPct: number,
  hoursDelta: number,
): number | null {
  if (hoursDelta <= 0) return null;
  const drainPerHr = (previousCapacityPct - currentCapacityPct) / hoursDelta;
  if (drainPerHr <= 0) return null; // charging or holding steady
  return currentCapacityPct / drainPerHr / 24;
}

/** How far the sensor sits above the tank floor: the tank plus its standoff. */
function sensorHeightAboveFloorCm(config?: UnitConfig): number | null {
  const tankHeight = config?.tankDimensions?.height;
  const mount = config?.sensorMountHeight;
  if (!tankHeight || mount === undefined || mount === null || mount < 0) return null;
  return tankHeight + mount;
}

/**
 * Why a reading cannot be turned into a depth.
 *   'above-tank'  — the surface would sit above the tank rim. The usual cause
 *                   is a sensor returning 0/near-0 when it gets no echo.
 *   'below-floor' — the surface would sit below the tank floor, so the tank
 *                   height or mount height is wrong, or the sensor is faulty.
 */
export type ReadingFault = 'above-tank' | 'below-floor' | null;

export function readingFault(measurementMm: number, config?: UnitConfig): ReadingFault {
  const sensorHeight = sensorHeightAboveFloorCm(config);
  const tankHeight = config?.tankDimensions?.height;
  if (sensorHeight === null || !tankHeight) return null;
  const depth = sensorHeight - measurementMm / 10;
  if (depth > tankHeight + DEPTH_FAULT_TOLERANCE_CM) return 'above-tank';
  if (depth < -DEPTH_FAULT_TOLERANCE_CM) return 'below-floor';
  return null;
}

/**
 * Water depth (cm) from a measurement (mm) and the tank config.
 *
 * The sensor is mounted `sensorMountHeight` above the tank TOP, so it looks
 * down from (tank height + mount height) and the water depth is that minus the
 * distance it reports.
 *
 * Returns null when unconfigured, and when the reading falls outside the tank
 * entirely. Both ends matter: a reading past the floor means the config is
 * wrong, and a reading above the rim is the shape a no-echo 0 takes. Rendering
 * either as a real depth turns a broken sensor into a confident number.
 */
export function computeWaterDepthCm(measurementMm: number, config?: UnitConfig): number | null {
  const sensorHeight = sensorHeightAboveFloorCm(config);
  const tankHeight = config?.tankDimensions?.height;
  if (sensorHeight === null || !tankHeight) return null;
  if (readingFault(measurementMm, config) !== null) return null;
  const depth = sensorHeight - measurementMm / 10;
  return Math.min(Math.max(0, depth), tankHeight);
}

function isHorizontal(shape?: string): boolean {
  return shape === 'cylindrical_horizontal';
}

/** Water volume (litres) for the given water depth (cm) and tank config. */
export function computeTankVolumeLiters(depthCm: number, config?: UnitConfig): number | null {
  const D = config?.tankDimensions?.diameter;
  const H = config?.tankDimensions?.height;
  if (!D || !H) return null;

  if (isHorizontal(config?.tankShape)) {
    const R = D / 2;
    const clampedD = Math.max(0, Math.min(depthCm, D));
    const angleTerm = Math.acos((R - clampedD) / R);
    const segmentArea = R * R * angleTerm - (R - clampedD) * Math.sqrt(2 * R * clampedD - clampedD * clampedD);
    return (segmentArea * H) / 1000;
  }

  // A sensor mounted above the tank top can imply a depth greater than the tank
  // is tall. Clamp to H so the reported volume can never exceed what the tank
  // physically holds — computePercentFilled already clamps to 100%, and the two
  // must not disagree.
  const radius = D / 2;
  const clampedDepth = Math.min(Math.max(0, depthCm), H);
  return (Math.PI * radius * radius * clampedDepth) / 1000;
}

/**
 * Total litres the tank holds. A vertical cylinder is full at its height, a
 * horizontal one at its diameter — deriving both from computeTankVolumeLiters
 * keeps capacity and current volume from drifting apart.
 */
export function computeTankCapacityLiters(config?: UnitConfig): number | null {
  const D = config?.tankDimensions?.diameter;
  const H = config?.tankDimensions?.height;
  if (!D || !H) return null;
  return computeTankVolumeLiters(isHorizontal(config?.tankShape) ? D : H, config);
}

/**
 * Percent full (0-100) — as a fraction of the water the tank holds, not of its
 * depth. The two are the same for a vertical cylinder but not for a horizontal
 * one, whose cross-section is a circle: a horizontal tank filled to a quarter
 * of its diameter holds 19.6% of its water, not 25%. Deriving this from the
 * volume keeps the figure consistent with the litres shown beside it.
 */
export function computePercentFilled(depthCm: number, config?: UnitConfig): number | null {
  const D = config?.tankDimensions?.diameter;
  const H = config?.tankDimensions?.height;
  if (!D || !H) return null;

  if (isHorizontal(config?.tankShape)) {
    const volume = computeTankVolumeLiters(depthCm, config);
    const capacity = computeTankVolumeLiters(D, config); // full when depth spans the diameter
    if (volume === null || !capacity) return null;
    return Math.min(100, (volume / capacity) * 100);
  }
  return Math.min(100, Math.max(0, depthCm / H) * 100);
}

/**
 * Which piece of tank config is preventing a water-volume calculation, or null
 * if the tank is fully configured. `0` counts as unset — a 0 diameter/height or
 * sensor-mount-height can't produce a real reading. Tank size is the more
 * fundamental gap, so it's reported first.
 */
export type TankConfigGap = 'size' | 'mount' | null;

export function tankConfigGap(config?: UnitConfig): TankConfigGap {
  const D = config?.tankDimensions?.diameter;
  const H = config?.tankDimensions?.height;
  if (!D || !H) return 'size';
  // 0 is a real value here — the sensor mounted level with the tank rim — so
  // only a missing or negative standoff counts as unset.
  const mount = config?.sensorMountHeight;
  if (mount === undefined || mount === null || mount < 0) return 'mount';
  return null;
}

/** Short call-to-action naming the missing tank config (for the dashboard's volume column). */
export function tankConfigGapLabel(config?: UnitConfig): string {
  return tankConfigGap(config) === 'mount' ? 'Set sensor mount height' : 'Set tank size';
}

/**
 * Why a tank has no water reading. Extends `tankConfigGap` with the case where
 * the config is complete but the reading contradicts it — a fully-configured
 * unit whose mount height is wrong needs a different call to action than one
 * that was never set up.
 */
export type WaterReadingGap = TankConfigGap | 'above-tank' | 'below-floor';

export function waterReadingGap(config?: UnitConfig, measurementMm?: number): WaterReadingGap {
  const configGap = tankConfigGap(config);
  if (configGap) return configGap;
  if (measurementMm !== undefined) return readingFault(measurementMm, config);
  return null;
}

/** Short call-to-action for the dashboard when no depth/volume can be shown. */
export function waterReadingGapLabel(config?: UnitConfig, measurementMm?: number): string {
  switch (waterReadingGap(config, measurementMm)) {
    case 'above-tank': return 'Sensor reading unavailable';
    case 'below-floor': return 'Check tank height / mount height';
    case 'mount': return 'Set sensor mount height';
    default: return 'Set tank size';
  }
}

/** Volume delta (litres): current minus an earlier volume. */
export function computeVolumeChangeLiters(currentVolumeLiters: number, pastVolumeLiters: number): number {
  return currentVolumeLiters - pastVolumeLiters;
}

/** Point-in-time metrics consumed by the backend alert evaluator. */
export interface PointMetrics {
  voltage: number;
  battery_capacity: number;
  charge_rate: number;
  battery_life: number | null;
  water_depth: number | null; // cm — null when it cannot be derived
  water_volume: number | null; // L — null when it cannot be derived
}

/**
 * Aggregate the point-in-time metrics the alert engine evaluates. Depth and
 * volume stay null when they cannot be derived so the evaluator skips those
 * rules rather than firing on a fabricated 0 — a unit with no tank config is
 * not a tank that has run dry. Voltage/charge-rate alerts are unaffected.
 */
export function computeAllMetrics(
  current: TelemetryPoint,
  previous: TelemetryPoint | null,
  config?: UnitConfig,
): PointMetrics {
  const capacity = getBatteryCapacity(current.voltage, config?.maxBatteryVoltage);
  const chargeRate = computeChargeRate(current, previous);

  let batteryLife: number | null = null;
  if (previous) {
    const prevCapacity = getBatteryCapacity(previous.voltage, config?.maxBatteryVoltage);
    batteryLife = estimateBatteryLifeDays(capacity, prevCapacity, hoursBetween(current.timestamp, previous.timestamp));
  }

  const depth = computeWaterDepthCm(current.measurement, config);
  const volume = depth === null ? null : computeTankVolumeLiters(depth, config);

  return {
    voltage: current.voltage,
    battery_capacity: capacity,
    charge_rate: Math.round(chargeRate * 1000) / 1000,
    battery_life: batteryLife,
    water_depth: depth === null ? null : Math.round(depth * 10) / 10,
    water_volume: volume === null ? null : Math.round(volume),
  };
}
